import {
    formatAppointmentDateTime,
    formatPatientName,
    getRowKey,
} from './cancellationFollowupFormatters';

export {
    formatAppointmentDateTime,
    formatPatientName,
    getRowKey,
} from './cancellationFollowupFormatters';

export const CANCELLATION_CHANNELS = [
    { key: 'email', label: 'Email', icon: 'bi-envelope', enabledKey: 'isEmail', contactKey: 'email', selectedKey: 'isEmailSelected', sendable: true },
    { key: 'sms', label: 'SMS', icon: 'bi-chat-dots', enabledKey: 'isSms', contactKey: 'cellPhone', selectedKey: 'isSmsSelected', sendable: true },
    { key: 'call', label: 'Call', icon: 'bi-telephone', enabledKey: 'isCall', contactKeys: ['cellPhone', 'homePhone'], sendable: false },
];

const hasContact = (record, channel) => {
    if (channel.contactKeys) {
        return channel.contactKeys.some((key) => Boolean(record?.[key]?.trim()));
    }

    return Boolean(record?.[channel.contactKey]?.trim());
};

export const resolvePreferredChannel = (record) => {
    if (record?.isEmailSelected) {
        return 'email';
    }

    if (record?.isSmsSelected) {
        return 'sms';
    }

    const ranked = CANCELLATION_CHANNELS
        .filter((channel) => channel.sendable && hasContact(record, channel) && record?.[channel.enabledKey] !== false)
        .sort((left, right) => {
            const leftPref = Number(record?.[left.key === 'email' ? 'prefEmail' : 'prefSms'] ?? 99);
            const rightPref = Number(record?.[right.key === 'email' ? 'prefEmail' : 'prefSms'] ?? 99);
            return leftPref - rightPref;
        });

    return ranked[0]?.key ?? null;
};

export const getChannelState = (record, channelKey) => {
    const channel = CANCELLATION_CHANNELS.find((item) => item.key === channelKey);
    if (!channel) {
        return { available: false, enabled: false, disabled: true, comingSoon: false };
    }

    const available = hasContact(record, channel);
    const followupEnabled = record?.isFollowup !== false;
    const channelEnabled = record?.[channel.enabledKey] !== false;

    if (!channel.sendable) {
        return {
            available,
            enabled: false,
            disabled: true,
            comingSoon: true,
        };
    }

    const disabled = !available || !followupEnabled || !channelEnabled;

    return {
        available,
        enabled: followupEnabled && channelEnabled,
        disabled,
        comingSoon: false,
    };
};

export const buildDefaultChannelSelection = (record) => {
    const preferred = resolvePreferredChannel(record);

    if (preferred && !getChannelState(record, preferred).disabled) {
        return preferred;
    }

    const fallback = CANCELLATION_CHANNELS.find(
        (channel) => channel.sendable && !getChannelState(record, channel.key).disabled
    );

    return fallback?.key ?? null;
};

export const getRowChannelSelection = (channelSelections, rowKey, record) =>
    channelSelections[rowKey] ?? buildDefaultChannelSelection(record);

export const isRowSendable = (record) => {
    if (record?.isFollowup === false) {
        return false;
    }

    return CANCELLATION_CHANNELS.some(
        (channel) => channel.sendable && !getChannelState(record, channel.key).disabled
    );
};

export const buildCancellationSendJobs = (records, selectedRowKeys, channelSelections) => {
    const jobs = [];

    records.forEach((record) => {
        const rowKey = getRowKey(record);

        if (!selectedRowKeys.has(rowKey)) {
            return;
        }

        const selectedChannel = getRowChannelSelection(channelSelections, rowKey, record);
        if (!selectedChannel || selectedChannel === 'call') {
            return;
        }

        const state = getChannelState(record, selectedChannel);
        if (state.disabled) {
            return;
        }

        jobs.push({
            patientId: record.id,
            appointmentId: record.appointmentId,
            mediumType: selectedChannel === 'sms' ? 'SMS' : 'EMAIL',
            patientName: formatPatientName(record),
        });
    });

    return jobs;
};

export const getSendableRowKeys = (records) =>
    records.filter(isRowSendable).map((record) => getRowKey(record));
